import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Input, Select, Textarea } from "../../components/ui";
import { DocumentationDraft, DocumentationFieldDefinition, DocumentationValidationIssue } from "./models";
import noteStyles from "../../components/NoteEditForm/NoteEditForm.module.css";
import styles from "./DocumentationWorkspace.module.css";

type Props = {
    draft: DocumentationDraft;
    edits: Record<string, unknown>;
    confirmations: Set<string>;
    disabled: boolean;
    readOnly?: boolean;
    onEdit: (fieldKey: string, value: unknown) => void;
    onConfirmation: (fieldKey: string, confirmed: boolean) => void;
};

/** Field types that are short enough to sit two-up, like the original date/time and deviation/note type rows. */
const SHORT_TYPES = new Set(["string", "integer", "number", "boolean", "date", "datetime", "enum", "client_context_reference"]);

function localized(values: Record<string, string> | undefined, language: string, fallback: string): string {
    return values?.[language] || values?.en || values?.sv || fallback;
}

function inputValue(draft: DocumentationDraft, edits: Record<string, unknown>, fieldKey: string): unknown {
    return Object.prototype.hasOwnProperty.call(edits, fieldKey) ? edits[fieldKey] : draft.fields[fieldKey]?.value;
}

/** Pairs consecutive short fields so the form keeps the two-column rhythm of the original note editor. */
function toRows(fields: DocumentationFieldDefinition[]): DocumentationFieldDefinition[][] {
    const rows: DocumentationFieldDefinition[][] = [];
    for (const field of fields) {
        const previous = rows[rows.length - 1];
        if (SHORT_TYPES.has(field.type) && previous && previous.length === 1 && SHORT_TYPES.has(previous[0].type)) {
            previous.push(field);
        } else {
            rows.push([field]);
        }
    }
    return rows;
}

export const DynamicDocumentationForm = ({ draft, edits, confirmations, disabled, readOnly = false, onEdit, onConfirmation }: Props) => {
    const { t, i18n } = useTranslation();
    const definition = draft.definition;
    const issuesByField = useMemo(() => {
        const grouped = new Map<string, DocumentationValidationIssue[]>();
        for (const issue of draft.validation?.issues || []) {
            if (!issue.field_key) continue;
            grouped.set(issue.field_key, [...(grouped.get(issue.field_key) || []), issue]);
        }
        return grouped;
    }, [draft.validation]);

    if (!definition) return null;

    const displayValue = (field: DocumentationFieldDefinition, value: unknown): string => {
        if (value === null || value === undefined || value === "") return "—";
        if (typeof value === "boolean") return value ? t("documentFlow.yes") : t("documentFlow.no");
        if (Array.isArray(value)) return value.join(", ");
        if (field.type === "goal_reference") {
            const goal = (draft.context_options?.active_goals || []).find(item => item.goal_id === value);
            return goal?.title || String(value);
        }
        return String(value);
    };

    const renderControl = (field: DocumentationFieldDefinition) => {
        const value = inputValue(draft, edits, field.field_key);
        const common = {
            id: `${draft.id}-${field.field_key}`,
            disabled: disabled || !field.employee_editable,
            "aria-invalid": (issuesByField.get(field.field_key) || []).some(issue => issue.severity === "error")
        };
        if (field.type === "boolean") {
            return (
                <Select
                    {...common}
                    className={noteStyles.noteEditInput}
                    value={value === true ? "true" : value === false ? "false" : ""}
                    onChange={event => onEdit(field.field_key, event.target.value === "" ? null : event.target.value === "true")}
                >
                    <option value="">—</option>
                    <option value="true">{t("documentFlow.yes")}</option>
                    <option value="false">{t("documentFlow.no")}</option>
                </Select>
            );
        }
        if (field.type === "enum") {
            return (
                <Select
                    {...common}
                    className={noteStyles.noteEditInput}
                    value={String(value ?? "")}
                    onChange={event => onEdit(field.field_key, event.target.value || null)}
                >
                    <option value="">—</option>
                    {field.constraints.enum_values.map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </Select>
            );
        }
        if (field.type === "goal_reference") {
            return (
                <Select
                    {...common}
                    className={noteStyles.noteEditInput}
                    value={String(value ?? "")}
                    onChange={event => onEdit(field.field_key, event.target.value || null)}
                >
                    <option value="">{t("documentFlow.selectGoal")}</option>
                    {(draft.context_options?.active_goals || []).map(goal => (
                        <option key={goal.goal_id} value={goal.goal_id}>
                            {goal.title || goal.goal_id}
                        </option>
                    ))}
                </Select>
            );
        }
        if (field.type === "text" || field.type === "string_list") {
            const display = field.type === "string_list" && Array.isArray(value) ? value.join("\n") : String(value ?? "");
            return (
                <Textarea
                    {...common}
                    className={noteStyles.noteEditTextarea}
                    rows={field.type === "text" ? 6 : 4}
                    maxLength={field.constraints.max_length ?? undefined}
                    value={display}
                    onChange={event =>
                        onEdit(
                            field.field_key,
                            field.type === "string_list"
                                ? event.target.value
                                      .split("\n")
                                      .map(item => item.trim())
                                      .filter(Boolean)
                                : event.target.value
                        )
                    }
                />
            );
        }
        const type =
            field.type === "integer" || field.type === "number"
                ? "number"
                : field.type === "datetime"
                  ? "datetime-local"
                  : field.type === "date"
                    ? "date"
                    : "text";
        return (
            <Input
                {...common}
                className={noteStyles.noteEditInput}
                type={type}
                value={String(value ?? "")}
                min={field.constraints.min_value ?? undefined}
                max={field.constraints.max_value ?? undefined}
                onChange={event => {
                    const next = event.target.value;
                    if (field.type === "integer") onEdit(field.field_key, next === "" ? null : Number.parseInt(next, 10));
                    else if (field.type === "number") onEdit(field.field_key, next === "" ? null : Number(next));
                    else onEdit(field.field_key, next);
                }}
            />
        );
    };

    const renderField = (field: DocumentationFieldDefinition) => {
        const stored = draft.fields[field.field_key];
        const value = inputValue(draft, edits, field.field_key);
        const needsConfirmation = field.source_policy === "employee_confirmation";
        const fieldIssues = issuesByField.get(field.field_key) || [];
        return (
            <div className={noteStyles.noteField} key={field.field_key}>
                <strong>
                    {localized(field.label, i18n.language, field.field_key)}
                    {field.required && !readOnly ? <span className={styles.required}> *</span> : null}:
                </strong>
                {field.description ? <p className={styles.description}>{field.description}</p> : null}
                {readOnly ? <span className={styles.fieldValue}>{displayValue(field, value)}</span> : renderControl(field)}
                {stored?.source ? (
                    <p className={styles.provenance}>
                        {t("documentFlow.source")}: {t(`documentFlow.fieldSource.${stored.source}`, { defaultValue: stored.source })}
                        {stored.evidence ? ` · “${stored.evidence}”` : ""}
                    </p>
                ) : null}
                {needsConfirmation && value != null ? (
                    <label className={styles.confirmation}>
                        <input
                            type="checkbox"
                            disabled={disabled || readOnly}
                            checked={confirmations.has(field.field_key) || Boolean(stored?.confirmed)}
                            onChange={event => onConfirmation(field.field_key, event.target.checked)}
                        />
                        {t("documentFlow.confirmField")}
                    </label>
                ) : null}
                {fieldIssues.map(issue => (
                    <p className={issue.severity === "error" ? styles.fieldError : styles.fieldWarning} key={`${issue.rule_key}-${issue.code}`}>
                        {issue.message}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <div className={noteStyles.noteFullContent}>
            <div className={noteStyles.noteEditForm}>
                {toRows(definition.fields).map(row =>
                    row.length === 2 ? (
                        <div className={noteStyles.noteEditFieldGrid} key={row[0].field_key}>
                            {row.map(renderField)}
                        </div>
                    ) : (
                        renderField(row[0])
                    )
                )}
            </div>
        </div>
    );
};
